import { useFormik } from "formik"
import React, { useEffect, useState } from "react"
import {
  Card,
  Col,
  FloatingLabel,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap"
import { toast } from "react-toastify"
import * as Yup from "yup"
import ModalComponent from "../../../components/Modal"
import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import { handleOnBlur, handleOnchange } from "../../../lib/GlobalMethods"
import { _alphabetSpaceRegex_ } from "../../../lib/Regex"
import CustomSelect from "../../../components/CustomSelect"

const AddEditAdminRole = ({
  isUpdate,
  setIsUpdate,
  show,
  setShow,
  handleClose,
  fetchRole,
  permissionData,
}) => {
  const [selectedId, setSelectedId] = useState([])
  const [loading, setLoading] = useState(false)
  const staticPermission = ["view", "add", "edit", "delete"]
  const scopeData = ["ALL", "OWN"]

  const validationSchema = Yup.object({
    role: Yup.string().required("Role is required"),
    data_scope: Yup.string().required("Scope Data is requried"),
    permission_id: Yup.array()
      .min(1, "Permission is required")
      .required("Permission is required"),
  })

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: isUpdate ? "PATCH" : "POST",
        endpoint: isUpdate
          ? `${admin.updateRole}${isUpdate?.id}`
          : admin.createRole,
        data: values,
      })
      if (response?.data?.code === 201 || response?.data?.code === 200) {
        toast.success(response.data.message)
        setSelectedId([])
        setShow(false)
        setIsUpdate(false)
        fetchRole()
        resetForm({ values: "" })
        setLoading(false)
      } else {
        setLoading(false)
        throw new Error(response.data.message)
      }
    } catch (error) {
      setLoading(false)
      toast.error(error.message)
    }
  }

  const formik = useFormik({
    initialValues: isUpdate
      ? {
          role: isUpdate?.role,
          permission_id: [],
          data_scope: isUpdate?.data_scope,
        }
      : {
          role: "",
          data_scope: "",
          permission_id: [],
        },
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  })

  const { errors, values, touched, setFieldValue, resetForm } = formik

  const getId = (id, isChecked, isArray) => {
    let updatedSelectedId = [...selectedId]

    const findModuleData = (permId) => {
      const moduleName = Object.keys(permissionData).find((module) =>
        permissionData[module].some((perm) => perm.id === permId),
      )
      return {
        moduleName,
        modulePermissions: permissionData[moduleName] || [],
      }
    }

    if (isChecked) {
      if (isArray) {
        id.forEach((permId) => {
          if (!updatedSelectedId.includes(permId)) {
            updatedSelectedId.push(permId)
          }
          const { modulePermissions } = findModuleData(permId)
          const viewPermission = modulePermissions.find(
            (perm) => perm.permission === "view",
          )
          if (
            viewPermission &&
            !updatedSelectedId.includes(viewPermission.id)
          ) {
            updatedSelectedId.push(viewPermission.id)
          }
        })
      } else {
        if (!updatedSelectedId.includes(id)) {
          updatedSelectedId.push(id)
        }
        const { modulePermissions } = findModuleData(id)
        const viewPermission = modulePermissions.find(
          (perm) => perm.permission === "view",
        )
        if (viewPermission && !updatedSelectedId.includes(viewPermission.id)) {
          updatedSelectedId.push(viewPermission.id)
        }
      }
    } else {
      if (isArray) {
        id.forEach((permId) => {
          updatedSelectedId = updatedSelectedId.filter(
            (item) => item !== permId,
          )
          const { modulePermissions } = findModuleData(permId)
          const viewPermission = modulePermissions.find(
            (perm) => perm.permission === "view",
          )
          if (viewPermission) {
            updatedSelectedId = updatedSelectedId.filter(
              (item) => !modulePermissions.some((perm) => perm.id === item),
            )
          }
        })
      } else {
        updatedSelectedId = updatedSelectedId.filter((item) => item !== id)
        const { modulePermissions } = findModuleData(id)
        const viewPermission = modulePermissions.find(
          (perm) => perm.permission === "view",
        )
        if (viewPermission && !updatedSelectedId.includes(viewPermission.id)) {
          updatedSelectedId = updatedSelectedId.filter(
            (item) => !modulePermissions.some((perm) => perm.id === item),
          )
        }
      }
    }
    setSelectedId([...new Set(updatedSelectedId)])
  }

  useEffect(() => {
    setSelectedId(isUpdate?.permissions?.map((perm) => perm.id) || [])
  }, [isUpdate])

  useEffect(() => {
    setFieldValue("permission_id", selectedId)
  }, [selectedId])

  return (
    <ModalComponent
      show={show}
      backdrop="static"
      animationFade={true}
      onHide={() => {
        handleClose()
        resetForm()
        setSelectedId([])
      }}
      loading={loading}
      modeltitle={isUpdate ? "Update Role" : "Add Role"}
      submitname={
        loading ? (
          <Spinner size="sm" animation="border" variant="white" />
        ) : (
          "Save"
        )
      }
      handleSubmit={formik?.handleSubmit}
      btnclosetext={"Cancel"}
      children={
        <Form noValidate>
          <Row xs={"auto"} md={"1"}>
            <Col xs={12}>
              <FloatingLabel
                controlId="nameAdmin"
                label={
                  <>
                    Role Name <span className="required_star">*</span>
                  </>
                }
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder=""
                  name="role"
                  autoComplete="false"
                  maxLength={20}
                  value={values.role}
                  onChange={(e) =>
                    handleOnchange(e, _alphabetSpaceRegex_, formik)
                  }
                  onBlur={(e) => handleOnBlur(e, formik)}
                  isInvalid={touched.role && errors.role}
                  isValid={touched.role && !errors.role}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                    }
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.role}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>
          </Row>

          <Row xs={"auto"} md={"1"}>
            <Col xs={12} className="mb-3">
              <CustomSelect
                value={
                  values?.data_scope
                    ? {
                        label: values.data_scope,
                        value: values.data_scope,
                      }
                    : null
                }
                handleSelectChange={(selectedOption) => {
                  setFieldValue("data_scope", selectedOption?.value)
                }}
                options={scopeData.map((item) => ({
                  label: item,
                  value: item,
                }))}
                isMulti={false}
                placeholder={"Select Scope Data"}
              />
              {touched.data_scope && errors.data_scope && (
                <div className="text-danger mt-1">{errors.data_scope}</div>
              )}
            </Col>
          </Row>
          <h6>
            Permissions <span className="required_star">*</span>
          </h6>
          <Card>
            <Card.Body>
              <Table
                className="rounded overflow-hidden mb-0 table-striped"
                responsive="md"
              >
                <thead>
                  <tr>
                    <th>Module Name</th>
                    <th className="text-center">View</th>
                    <th className="text-center">Add</th>
                    <th className="text-center">Edit</th>
                    <th className="text-center">Delete</th>
                    <th className="text-center">Check All</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(permissionData).map((key, index) => {
                    const namePerm = permissionData[key]
                    return (
                      <tr key={index + 1}>
                        <td>{key.charAt(0).toUpperCase() + key.slice(1)}</td>
                        {staticPermission.map((permItem, idx) => {
                          const exists = namePerm.some(
                            (perm) => perm.permission === permItem,
                          )
                          const id = namePerm?.find(
                            (item) => item?.permission === permItem,
                          )?.id
                          return (
                            <td className="text-center" key={idx}>
                              {exists ? (
                                <Form.Check
                                  type="checkbox"
                                  id={id}
                                  checked={selectedId.includes(id)}
                                  onChange={(e) => {
                                    getId(id, e.target.checked)
                                  }}
                                />
                              ) : (
                                "-"
                              )}
                            </td>
                          )
                        })}
                        <td className="text-center">
                          <Form.Check
                            type="checkbox"
                            id="all"
                            onClick={(e) => {
                              const ids = namePerm.map((perm) => perm.id)
                              getId(ids, e.target.checked, true)
                            }}
                            checked={namePerm.every((perm) =>
                              selectedId.includes(perm.id),
                            )}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
              {touched?.permission_id && errors?.permission_id && (
                <p className="invalid_error">{errors.permission_id}</p>
              )}
            </Card.Body>
          </Card>
        </Form>
      }
    />
  )
}

export default AddEditAdminRole
